import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { PopupRenderer } from "./renderer/PopupRenderer";
import { TeaserTab } from "./renderer/TeaserTab";
import { normalizeDesign } from "./renderer/normalizeDesign";
import { RENDERER_CSS, ensureFontsLoaded } from "./renderer/engine";
import {
  PopupSettings,
  PopupState,
  PopupFormData,
  PopupWidgetConfig,
  PreviewMode,
  PopupWheelSpinResult,
} from "@/shared/types/popup-types";
import type { WheelStatus } from "./renderer/WheelStage";
import { WHEEL_DEFAULTS, wheelFormPlacement } from "@/lib/wheel";
import type { IssuedPass } from "@/lib/popup-api";
import {
  fetchPopupSettings,
  submitPopupData,
  requestWheelSpin,
  fetchIssuedPass,
  fetchIssuedDiscountCode,
  reportPopupDisplay,
  PopupApiError,
} from "@/lib/popup-api";
import { raisePopupHost } from "@/lib/host-stacking";
import { matchHostToWindow } from "@/lib/host-zoom";
import { isFlowiqChatOpen, whenChatClosed } from "@/lib/chat-presence";
import { setClickTrigger } from "@/lib/click-trigger";
import { countPageView } from "@/lib/page-views";
import {
  shouldShowPopup,
  getVisitorInfo,
  setupScrollTrigger,
  setupTimerTrigger,
  setupExitIntentTrigger,
  recordPopupImpression,
  recordPopupDismissal,
  recordPopupSubmission,
  getPopupLifecycle,
  recordIssuedReward,
} from "@/lib/popup-rules-engine";
import {
  firstPopupStep,
  stepAfterIntro,
  introCtaUrl,
  stepAfterScratch,
  stepAfterWelcome,
  phonePagePlayable,
  stepAfterQuestion,
  playableQuestions,
  stepAfterWheel,
} from "@/lib/popup-steps";
import { useShadowDom } from "@/shared/context/shadow-dom-context";
import {
  emitPopupLoaded,
  emitPopupShown,
  emitPopupClosed,
  emitPopupSubmitted,
  emitPopupCtaClick,
  emitPopupError,
  emitPopupTeaserShown,
  emitPopupTeaserClick,
} from "@/lib/gtm-events";

export interface PopupWidgetProps {
  organizationId: string;
  // Fallback popup id. When activeOnStore is set, the org's live popup is preferred and this
  // is only used if none is flagged live; otherwise this targets a specific popup.
  popupId?: string;
  // When true, resolve the org's currently-active-on-store popup (with popupId as fallback).
  activeOnStore?: boolean;
  config?: PopupWidgetConfig;
  onImpression?: () => void;
  onSubmission?: (data: PopupFormData) => void;
  onClose?: () => void;
  debug?: boolean;
  previewMode?: PreviewMode;
  shopifyCustomerId?: string;
  /** Draft settings pushed by the builder's live-preview bridge. When set,
   *  fetching and rules are skipped and these settings render immediately. */
  overrideSettings?: PopupSettings | null;
  /** Builder drag-resize overlay. Only the preview bridge ever sets this. */
  editMode?: boolean;
  /** Opened on demand by the visitor (the chat widget's "popup" quick action
   *  calls window.ReactPopupWidget.open()). Rides on the same forced-show path
   *  as the hosted landing page — no triggers, no frequency cap, form fully
   *  live — and only changes the analytics trigger, so an on-demand open is
   *  never mistaken for a shared landing-page view. */
  onDemand?: boolean;
  /** On-demand only: the popup could not be shown (a returning customer the
   *  server skips, or no live popup). Lets the caller say so instead of
   *  leaving the visitor tapping a button that does nothing. */
  onUnavailable?: (reason: string) => void;
}

export const PopupWidget: React.FC<PopupWidgetProps> = ({
  organizationId,
  popupId,
  activeOnStore,
  config: _unusedConfig,
  onImpression,
  onSubmission,
  onClose,
  debug = false,
  previewMode,
  shopifyCustomerId,
  overrideSettings,
  editMode,
  onDemand,
  onUnavailable,
}) => {
  const [popupState, setPopupState] = useState<PopupState>({
    isLoading: true,
    isVisible: false,
    currentStep: "closed",
    hasBeenShown: false,
  });

  const [settings, setSettings] = useState<PopupSettings | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [teaserVisible, setTeaserVisible] = useState(false);
  const [teaserAnnounced, setTeaserAnnounced] = useState(false);
  // Post-signup reward mode: the tab is a way BACK to the visitor's code/pass
  // (teaser.postSignup === "reward"), not an invitation to sign up again.
  const [teaserReward, setTeaserReward] = useState(false);
  // The discount code minted for THIS visitor. Filled in shortly after they
  // submit (see pollForIssuedCode); null means "show the message copy instead".
  const [issuedDiscountCode, setIssuedDiscountCode] = useState<string | null>(null);
  // undefined = no poll ran (static popups); 'pending' while fetching; 'done' after.
  const [issuedCodeStatus, setIssuedCodeStatus] = useState<"pending" | "done" | undefined>(undefined);
  // The wallet pass issued for THIS signup, read back from the server. The
  // widget used to derive the serial itself; see fetchIssuedPass for why not.
  const [issuedPass, setIssuedPass] = useState<IssuedPass | null>(null);
  const [issuedPassStatus, setIssuedPassStatus] = useState<"pending" | "done" | undefined>(undefined);
  const codePollCancelled = useRef(false);
  const passPollCancelled = useRef(false);
  // Holds the gate's form data while the swipe deck plays: the lead is only
  // persisted ONCE, at deck completion, with the card verdicts merged in.
  // (Also the lead for a "wheel after the form" popup until the spin.)
  const pendingGateFormRef = useRef<PopupFormData | null>(null);
  // Question-page answers, held until the ONE real submission (like the deck's
  // verdicts) — an abandoned flow never creates a half-lead.
  const pendingAnswersRef = useRef<Record<string, string>>({});
  // The payload of the submission that FAILED, so the deck's "Try again" can
  // re-fire it. The deck clears pendingGateFormRef when it completes, so
  // without this a failed submission took the lead with it (22 Sep 2026).
  const failedSubmissionRef = useRef<PopupFormData | null>(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  // Spin-to-win lifecycle. The widget owns it because it owns the network:
  // spinning while the submission is in flight, result once the server has
  // named the wedge, error if the submit failed (the wheel coasts to a stop
  // and the form shows the message), idle in between.
  const [wheelStatus, setWheelStatus] = useState<WheelStatus>("idle");
  const [wheelResult, setWheelResult] = useState<PopupWheelSpinResult | null>(null);
  const [wheelExpiresAt, setWheelExpiresAt] = useState<string | null>(null);
  const wheelLandedRef = useRef(false);
  // Play-first games: the server-signed prize, carried from the game page to
  // the form's submission (cleared only once that submission succeeds, so a
  // rejected email keeps the prize the visitor already saw).
  const wheelTokenRef = useRef<string | null>(null);
  useEffect(
    () => () => {
      codePollCancelled.current = true;
      passPollCancelled.current = true;
    },
    []
  );

  const { host } = useShadowDom();

  // The active popup is resolved from the organisation server-side; once settings
  // load, settings.id is the authoritative popup id for submissions/analytics.
  const resolvedPopupId = settings?.id ?? popupId;

  // One normalized design for any row shape (legacy or enriched).
  const normalized = useMemo(
    () =>
      settings
        ? normalizeDesign(
            settings.style_settings as unknown as Parameters<typeof normalizeDesign>[0]
          )
        : null,
    [settings]
  );

  const design = normalized?.design;

  // The teaser can mount before the popup (teaser_first), so make sure the
  // theme font — Google or org-uploaded custom — is loading as soon as the
  // design is known. The renderer loads the full per-element set itself.
  useEffect(() => {
    if (design) ensureFontsLoaded([design.theme.fontFamily], design.customFonts);
  }, [design]);

  // Only blocking formats may flip the host to pointer-events:auto — bars,
  // slide-ins and teasers must leave the merchant's page clickable.
  const isBlockingFormat =
    !design || design.format === "modal" || design.format === "fullscreen";

  useEffect(() => {
    if (!host) {
      return;
    }

    host.setAttribute(
      "data-popup-active",
      popupState.isVisible && isBlockingFormat ? "true" : "false"
    );
  }, [host, popupState.isVisible, isBlockingFormat]);

  useEffect(() => {
    return () => {
      if (host) {
        host.setAttribute("data-popup-active", "false");
      }
    };
  }, [host]);

  // Re-read the store's page zoom as the popup or teaser appears: the theme's
  // stylesheet may have landed after the host was made (lib/host-zoom).
  useEffect(() => {
    if (host && (popupState.isVisible || teaserVisible)) matchHostToWindow(host);
  }, [host, popupState.isVisible, teaserVisible]);

  // ---------- live-preview override (builder bridge) ----------

  useEffect(() => {
    if (!overrideSettings) return;
    setSettings(overrideSettings);
    if (previewMode === "teaser") {
      // Teaser preview: show only the edge tab, popup hidden.
      setTeaserVisible(true);
      setPopupState((prev) => ({
        ...prev,
        isLoading: false,
        isVisible: false,
        currentStep: "closed",
        hasBeenShown: true,
      }));
      return;
    }
    setTeaserVisible(false);
    setPopupState((prev) => ({
      ...prev,
      isLoading: false,
      isVisible: true,
      currentStep:
        previewMode === "success"
          ? "success"
          : previewMode === "deck"
            ? "deck"
            : previewMode === "scratch"
              ? "scratch"
              : previewMode === "question"
                ? "question"
              : previewMode === "phone"
                ? "phone"
              : previewMode === "wheel"
                ? "wheel"
                : previewMode === "intro"
                  ? "intro"
                  : ("welcome" as const),
      hasBeenShown: true,
    }));
  }, [overrideSettings, previewMode]);

  // A blocking popup must paint above the chat widget, which now also sits at
  // the max z-index (18 Sep 2026) — at a tie, document order decides. Only the
  // blocking layer raises; the teaser stays put so it cannot cover an open
  // chat panel. See lib/host-stacking.
  useEffect(() => {
    if (popupState.isVisible) raisePopupHost();
  }, [popupState.isVisible]);

  // ---------- initialization ----------

  useEffect(() => {
    // Preview override renders synchronously from props — skip fetch + rules.
    if (overrideSettings) return;

    let cleanupTimer: (() => void) | null = null;
    let cleanupScroll: (() => void) | null = null;
    let cleanupExitIntent: (() => void) | null = null;
    let cleanupChatHold: (() => void) | null = null;

    const initializePopup = async () => {
      try {
        setPopupState((prev) => ({ ...prev, isLoading: true }));

        // Fetch settings
        let settingsResponse;
        try {
          settingsResponse = await fetchPopupSettings(
            organizationId,
            popupId,
            shopifyCustomerId,
            activeOnStore,
            undefined,
            previewMode === "page"
          );
        } catch (error) {
          console.error("Failed to fetch popup settings:", error);
          emitPopupError(
            { organizationId, popupId, debug },
            {
              error_type: "settings_failed",
              message: error instanceof Error ? error.message : String(error),
            }
          );
          setPopupState((prev) => ({ ...prev, isLoading: false }));
          return;
        }

        // Check if popup should be skipped for returning customers
        if (settingsResponse.skipPopup) {
          if (debug) {
            console.log("Popup skipped server-side (returning customer or country rule)");
          }
          setPopupState((prev) => ({ ...prev, isLoading: false }));
          // A first-order offer SHOULD stay hidden from a returning customer,
          // so the skip is correct — but an on-demand caller has a button
          // sitting there and needs to be told, not left silent.
          onUnavailable?.("skipped");
          return;
        }

        const fetchedSettings = settingsResponse.data;
        if (!fetchedSettings) {
          setPopupState((prev) => ({ ...prev, isLoading: false }));
          onUnavailable?.("no_popup");
          return;
        }

        setSettings(fetchedSettings);

        // Popup is configured and ready on the page. Fire `loaded` once
        // regardless of whether rules end up showing it — lets clients
        // confirm the widget booted during GTM Preview. Skipped in
        // non-customer preview modes (admin panel previewing the design).
        if (!previewMode || previewMode === "customer" || previewMode === "page") {
          emitPopupLoaded({ organizationId, popupId: fetchedSettings.id, debug });
        }

        // Hosted landing page (flowstore.co.za/p/…): show the welcome step
        // immediately — no triggers, frequency caps or teaser lifecycle (a
        // shared link must always render) — but keep the form fully live.
        if (previewMode === "page") {
          setPopupState((prev) => ({
            ...prev,
            isLoading: false,
            isVisible: true,
            currentStep: firstPopupStep(fetchedSettings.content_settings),
            hasBeenShown: true,
            showTime: new Date().toISOString(),
          }));

          // Landing pages count a real impression too — the settings fetch
          // no longer increments (10 Aug 2026).
          reportPopupDisplay(fetchedSettings.id);
          if (onImpression) {
            onImpression();
          }
          emitPopupShown(
            { organizationId, popupId: fetchedSettings.id, debug },
            { trigger_type: onDemand ? "on_demand" : "landing_page" }
          );

          if (debug) {
            console.log(
              onDemand ? "On-demand open activated" : "Landing-page mode activated"
            );
          }
          return;
        }

        // Handle static preview modes (welcome/success/teaser) - show immediately
        if (previewMode && previewMode !== "customer") {
          if (previewMode === "teaser") {
            setTeaserVisible(true);
            setPopupState((prev) => ({ ...prev, isLoading: false }));
          } else {
            setPopupState((prev) => ({
              ...prev,
              isLoading: false,
              isVisible: true,
              currentStep: previewMode,
              hasBeenShown: true,
              showTime: new Date().toISOString(),
            }));
          }

          if (debug) {
            console.log("Static preview mode activated:", previewMode);
          }
          return;
        }

        // On the store itself. Before the lifecycle checks below, so they hold
        // for every visitor: the store's own button opens the popup (rules
        // when_to_show.open_on_click, click-trigger.ts), and this page counts
        // towards "show from the Nth page" (page-views.ts).
        setClickTrigger(fetchedSettings.rules_settings?.when_to_show?.open_on_click, () => {
          void window.ReactPopupWidget?.open();
        });
        countPageView();

        // Customer mode continues to normal rules processing
        if (previewMode === "customer" && debug) {
          console.log("Customer mode: applying all rules normally");
        }

        const design = normalizeDesign(
          fetchedSettings.style_settings as unknown as Parameters<typeof normalizeDesign>[0]
        ).design;
        const teaserFirst =
          design.teaser.enabled && design.teaser.mode === "teaser_first";
        const teaserConfigured =
          design.teaser.enabled && design.teaser.mode !== "off";
        const lifecycle = getPopupLifecycle(fetchedSettings.id);

        // Visitors who already submitted get neither popup nor teaser — on
        // EVERY popup. This check used to sit inside the teaserConfigured
        // gate below, so popups without a teaser re-prompted converted
        // subscribers forever (audit 10 Aug 2026).
        if (lifecycle.submissionCount > 0) {
          // postSignup "reward" keeps the tab alive as the visitor's way back
          // to their code/pass; anything else is the legacy full suppression.
          if (teaserConfigured && design.teaser.postSignup === "reward") {
            if (debug) console.log("Converted visitor: showing reward teaser");
            setPopupState((prev) => ({ ...prev, isLoading: false }));
            setTeaserReward(true);
            setTeaserVisible(true);
            return;
          }
          if (debug) console.log("Popup skipped: visitor already submitted");
          setPopupState((prev) => ({ ...prev, isLoading: false }));
          return;
        }

        // Returning visitors: once someone closed the popup without
        // submitting, the TEASER takes over on every later page load — the
        // offer stays reachable without the popup re-opening. With no teaser
        // configured, the frequency rules govern instead.
        if (teaserConfigured && lifecycle.dismissedAt) {
          if (debug) console.log("Returning dismissed visitor: showing teaser");
          setPopupState((prev) => ({ ...prev, isLoading: false }));
          setTeaserVisible(true);
          return;
        }

        // Normal mode - check rules
        const visitor = getVisitorInfo(fetchedSettings.id);
        const rulesResult = shouldShowPopup(
          fetchedSettings.rules_settings,
          visitor
        );

        if (debug) {
          console.log("Popup rules result:", rulesResult);
          console.log(
            "Trigger type:",
            fetchedSettings.rules_settings.when_to_show.trigger_type
          );
        }

        if (!rulesResult.shouldShow) {
          setPopupState((prev) => ({
            ...prev,
            isLoading: false,
            isVisible: false,
          }));
          // Frequency caps suppress the POPUP, not the offer: when a teaser
          // is configured, it shows instead so the visitor can still opt in.
          // Device/location blocks suppress both (targeting choices).
          if (
            teaserConfigured &&
            rulesResult.reason?.startsWith("Frequency") &&
            lifecycle.submissionCount === 0
          ) {
            if (debug) console.log("Popup frequency-capped: showing teaser instead");
            setTeaserVisible(true);
          }
          return;
        }

        // Function to show popup (used by all trigger types)
        const showPopup = () => {
          setTeaserVisible(false);
          setPopupState((prev) => ({
            ...prev,
            isLoading: false,
            isVisible: true,
            currentStep: firstPopupStep(fetchedSettings.content_settings),
            hasBeenShown: true,
            showTime: new Date().toISOString(),
          }));

          // Record impression in local storage
          recordPopupImpression(fetchedSettings.id);

          // Real impression: the popup is actually on screen (10 Aug 2026 —
          // total_impressions used to be counted by the settings fetch, i.e.
          // page loads). showPopup only runs on real visits: static previews
          // returned earlier and the builder's override path skips init.
          reportPopupDisplay(fetchedSettings.id);

          // Track impression
          if (onImpression) {
            onImpression();
          }

          emitPopupShown(
            { organizationId, popupId: fetchedSettings.id, debug },
            {
              trigger_type:
                fetchedSettings.rules_settings.when_to_show.trigger_type,
            }
          );

          if (debug) {
            console.log("Popup shown to visitor");
          }
        };

        // In teaser-first mode the trigger reveals the TEASER; the popup only
        // opens from a teaser click.
        const revealOnTrigger = () => {
          if (teaserFirst) {
            setPopupState((prev) => ({ ...prev, isLoading: false }));
            setTeaserVisible(true);
            if (debug) console.log("Teaser-first mode: teaser revealed");
          } else {
            showPopup();
          }
        };

        // A trigger that fires while the FlowIQ chat is open waits for the
        // chat to close instead of covering the conversation (lib/chat-presence).
        const onTriggerFired = () => {
          if (debug && isFlowiqChatOpen()) {
            console.log("Chat is open: holding the popup until it closes");
          }
          cleanupChatHold?.();
          cleanupChatHold = whenChatClosed(revealOnTrigger);
        };

        // Handle different trigger types
        const triggerType =
          fetchedSettings.rules_settings.when_to_show.trigger_type;

        if (triggerType === "scroll") {
          // Scroll-based trigger
          const scrollPercentage =
            fetchedSettings.rules_settings.when_to_show.scroll_percentage || 50;

          if (debug) {
            console.log("Setting up scroll trigger at", scrollPercentage, "%");
          }

          setPopupState((prev) => ({ ...prev, isLoading: false }));

          cleanupScroll = setupScrollTrigger(
            fetchedSettings.rules_settings,
            onTriggerFired
          );
        } else if (triggerType === "exit_intent" && visitor.device === "desktop") {
          // Exit-intent (desktop): fire when the cursor leaves through the top
          if (debug) {
            console.log("Setting up exit-intent trigger");
          }

          setPopupState((prev) => ({ ...prev, isLoading: false }));

          cleanupExitIntent = setupExitIntentTrigger(onTriggerFired);
        } else {
          // Timer-based trigger (default; also the exit-intent mobile fallback)
          const delay = rulesResult.delay || 0;

          if (debug) {
            console.log("Setting up timer trigger with delay:", delay, "seconds");
          }

          setPopupState((prev) => ({ ...prev, isLoading: false }));

          cleanupTimer = setupTimerTrigger(delay, onTriggerFired);
        }
      } catch (error) {
        console.error("Error initializing popup:", error);
        setPopupState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initializePopup();

    // Cleanup function
    return () => {
      if (cleanupTimer) {
        cleanupTimer();
      }
      if (cleanupScroll) {
        cleanupScroll();
      }
      if (cleanupExitIntent) {
        cleanupExitIntent();
      }
      if (cleanupChatHold) {
        cleanupChatHold();
      }
    };
  }, [
    organizationId,
    popupId,
    activeOnStore,
    debug,
    onImpression,
    previewMode,
    overrideSettings,
    onDemand,
    onUnavailable,
  ]);

  // Teaser analytics — announce once per page view
  useEffect(() => {
    if (teaserVisible && !teaserAnnounced && settings) {
      setTeaserAnnounced(true);
      emitPopupTeaserShown(
        { organizationId, popupId: settings.id, debug },
        { mode: normalized?.design.teaser.mode }
      );
    }
  }, [teaserVisible, teaserAnnounced, settings, organizationId, debug, normalized]);

  // ---------- handlers ----------

  /**
   * The unique code is minted by the discount webhook a beat AFTER the submission
   * is saved, so it cannot be known when the success screen first renders. Poll
   * briefly and fill it in. Only runs for popups that actually show a code box,
   * so popups without one make no extra requests at all. Giving up is silent by
   * design: the screen falls back to its message copy ("check your WhatsApp").
   */
  const pollForIssuedCode = useCallback(
    async (submissionId: string) => {
      const wantsCode =
        settings?.content_settings?.success?.discount_code?.enabled === true;
      if (!wantsCode || !submissionId) return;

      setIssuedCodeStatus("pending");

      // Measured 21 Aug 2026: a real mint (Shopify discount create + write-back)
      // took 5.8s, so a 6s budget only just fits and a slower store would miss it.
      // ~15s gives real headroom; the visitor is still reading the screen, and the
      // read endpoint stays open far longer than this.
      for (let attempt = 0; attempt < 30; attempt++) {
        if (codePollCancelled.current) return;

        const code = await fetchIssuedDiscountCode(organizationId, submissionId);
        if (code) {
          if (!codePollCancelled.current) {
            setIssuedDiscountCode(code);
            setIssuedCodeStatus("done");
          }
          // Remembered even if the visitor closed the popup mid-poll — the
          // reward teaser needs it on a later visit either way.
          if (settings?.id) recordIssuedReward(settings.id, { issuedCode: code });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Poll exhausted without a code — tell the renderer so its loading state
      // can settle on the "check your WhatsApp" fallback instead of spinning.
      if (!codePollCancelled.current) setIssuedCodeStatus("done");
    },
    [organizationId, settings]
  );

  /**
   * The wallet pass for THIS signup.
   *
   * Same shape as the discount poll above and for the same reason: the pass is
   * issued by the `issue-pass-popup` webhook a beat after the submission lands.
   * What changed (27 Aug 2026) is where the serial comes from — the widget used
   * to DERIVE it, mirroring the edge function's hash, which broke the moment the
   * server started reusing a returning member's existing card. The server is now
   * the only thing that decides which card a signup gets.
   */
  const pollForIssuedPass = useCallback(
    async (submissionId: string) => {
      if (!submissionId) return;
      setIssuedPassStatus("pending");

      for (let attempt = 0; attempt < 30; attempt++) {
        if (passPollCancelled.current) return;

        const pass = await fetchIssuedPass(organizationId, submissionId);
        if (pass) {
          if (!passPollCancelled.current) {
            setIssuedPass(pass);
            setIssuedPassStatus("done");
          }
          if (settings?.id) recordIssuedReward(settings.id, { issuedPass: pass });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Exhausted: the pass almost certainly exists, we just did not see it in
      // time. The screen settles on its "check your WhatsApp" copy rather than
      // spinning forever or, worse, linking somewhere we only guessed at.
      if (!passPollCancelled.current) setIssuedPassStatus("done");
    },
    [organizationId, settings]
  );

  // The real submission: persist the lead, poll for the minted code/pass, move
  // to the success step. Reached from the gate directly (classic popups) or
  // from deck completion (deck popups, with card verdicts in extra_fields).
  const runSubmission = useCallback(
    async (
      formData: PopupFormData,
      options?: { deferSuccess?: boolean }
    ): Promise<{ id: string; wheel?: PopupWheelSpinResult } | null> => {
      setGlobalError(null);
      setIssuedDiscountCode(null);
      setIssuedCodeStatus(undefined);

      // Submissions require the resolved popup id. Settings always load before the
      // welcome step is shown, so settings.id is available here.
      if (!settings?.id) {
        return null;
      }
      const activePopupId = settings.id;

      try {
        // Keep the welcome popup visible but show loading state
        setPopupState((prev) => ({
          ...prev,
          isLoading: true,
          submissionData: formData,
        }));

        // Play-first game already showed its prize: whichever path fires the
        // submission (form, phone page, deck), it carries the server's signed
        // token so the recorded prize is exactly the one the visitor saw.
        if (wheelTokenRef.current) {
          formData = { ...formData, wheel_token: wheelTokenRef.current };
        }
        // Question-page answers ride onto the same submission (never a
        // separate write), under the form's own extra_fields.
        const answers = pendingAnswersRef.current;
        if (Object.keys(answers).length) {
          formData = {
            ...formData,
            extra_fields: { ...answers, ...(formData.extra_fields || {}) },
          };
        }
        // Submit form data
        const response = await submitPopupData(
          organizationId,
          activePopupId,
          formData
        );

        // The signed prize has been recorded; a later signup starts clean.
        wheelTokenRef.current = null;
        // Fetch the code minted for THIS person so the success screen shows the
        // same one that goes to their WhatsApp. Deliberately not awaited: the
        // thank-you screen must appear immediately either way.
        void pollForIssuedCode(response.id);
        void pollForIssuedPass(response.id);

        if (options?.deferSuccess) {
          // Spin-to-win: the wheel still has to LAND on the wedge the server
          // picked — the success step waits for onWheelLanded. Fields stay
          // locked via wheelStatus, not the loading flag.
          setPopupState((prev) => ({ ...prev, isLoading: false }));
        } else {
          // Start transition state
          setPopupState((prev) => ({
            ...prev,
            isTransitioning: true,
          }));

          // Smoothly transition to success popup after a brief delay
          setTimeout(() => {
            setPopupState((prev) => ({
              ...prev,
              isLoading: false,
              isTransitioning: false,
              currentStep: "success",
            }));
            setGlobalError(null);
          }, 300); // Brief delay for smooth transition
        }

        // Converted visitors get neither popup nor teaser on future loads.
        recordPopupSubmission(activePopupId);

        // With postSignup "reward" the tab must take over in THIS session too:
        // the flag was previously only computed on page load, so the reward
        // teaser only appeared after a refresh.
        const teaserCfg = normalized?.design.teaser;
        if (
          teaserCfg?.enabled &&
          teaserCfg.mode !== "off" &&
          teaserCfg.postSignup === "reward"
        ) {
          setTeaserReward(true);
        }

        // Call submission callback
        if (onSubmission) {
          onSubmission(formData);
        }

        emitPopupSubmitted(
          { organizationId, popupId: activePopupId, debug },
          {
            fields_submitted: Object.keys(formData).filter(
              (key) =>
                formData[key as keyof PopupFormData] != null &&
                String(formData[key as keyof PopupFormData]).length > 0
            ),
          }
        );

        if (debug) {
          console.log("Popup submitted successfully:", response);
        }
        failedSubmissionRef.current = null;
        return response;
      } catch (error) {
        console.error("Error submitting popup:", error);

        // Keep the payload so "Try again" can re-fire it — on a deck or a
        // scratch card there is nothing left on screen to re-submit from.
        failedSubmissionRef.current = formData;

        // Reset loading state
        setPopupState((prev) => ({
          ...prev,
          isLoading: false,
        }));

        const fallbackMessage =
          "We couldn't submit the form. Please check your connection and try again.";

        setGlobalError(
          error instanceof PopupApiError ? error.message : fallbackMessage
        );

        emitPopupError(
          { organizationId, popupId: activePopupId, debug },
          {
            error_type: "submit_failed",
            message: error instanceof Error ? error.message : String(error),
          }
        );
        return null;
      }
    },
    [organizationId, settings?.id, onSubmission, debug, pollForIssuedCode, normalized]
  );

  // ---------- spin-to-win ----------

  // The spin. Inline placement hands us the validated form; "before"
  // placement spins the lead held by the welcome form. Either way ONE
  // submission fires, the server picks the wedge, and the wheel is told
  // where to land. On failure the wheel coasts to a stop and the form
  // shows the error (a duplicate email, a network drop) with fields unlocked.
  const handleWheelSpin = useCallback(
    async (formData?: PopupFormData) => {
      // Play-first: no lead yet. The server decides + signs the prize now;
      // the form comes after the reveal and carries the token.
      if (wheelFormPlacement(settings?.content_settings) === "after") {
        const pid = settings?.id;
        if (!pid || wheelStatus !== "idle") return;
        wheelLandedRef.current = false;
        setWheelResult(null);
        setGlobalError(null);
        setWheelStatus("spinning");
        emitPopupCtaClick({ organizationId, popupId: pid, debug }, { step: "wheel" });
        try {
          const spin = await requestWheelSpin(organizationId, pid);
          wheelTokenRef.current = spin.token;
          setWheelResult(spin.wheel);
          recordIssuedReward(pid, {
            wheelResult: spin.wheel,
            ...(spin.wheel.code ? { issuedCode: spin.wheel.code } : {}),
          });
          if (spin.wheel.code) {
            setIssuedDiscountCode(spin.wheel.code);
            setIssuedCodeStatus("done");
          }
          setWheelStatus("result");
        } catch (e) {
          setGlobalError(e instanceof Error ? e.message : "Could not start the game");
          setWheelStatus("error");
        }
        return;
      }
      const data = formData ?? pendingGateFormRef.current;
      if (!data || wheelStatus !== "idle") return;
      wheelLandedRef.current = false;
      setWheelResult(null);
      setWheelStatus("spinning");
      emitPopupCtaClick(
        { organizationId, popupId: settings?.id || resolvedPopupId, debug },
        { step: "wheel" }
      );
      const response = await runSubmission(data, { deferSuccess: true });
      if (!response) {
        setWheelStatus("error");
        return;
      }
      pendingGateFormRef.current = null;
      if (!response.wheel) {
        // The server did not treat this as a wheel popup (config changed
        // between load and spin) — fall through to the classic finish.
        setWheelStatus("idle");
        setPopupState((prev) => ({ ...prev, isTransitioning: true }));
        setTimeout(() => {
          setPopupState((prev) => ({ ...prev, isTransitioning: false, currentStep: "success" }));
        }, 300);
        return;
      }
      setWheelResult(response.wheel);
      // Urgency timer: minted ONCE per visitor, persisted, never restarted.
      const minutes = settings?.content_settings?.wheel?.countdown_minutes;
      const expires =
        response.wheel.win && typeof minutes === "number" && minutes > 0
          ? new Date(Date.now() + minutes * 60_000).toISOString()
          : null;
      setWheelExpiresAt(expires);
      if (settings?.id) {
        recordIssuedReward(settings.id, {
          wheelResult: response.wheel,
          ...(expires ? { wheelExpiresAt: expires } : {}),
          ...(response.wheel.code ? { issuedCode: response.wheel.code } : {}),
        });
      }
      // A static per-wedge code is known right now — show it without polling.
      if (response.wheel.code) {
        setIssuedDiscountCode(response.wheel.code);
        setIssuedCodeStatus("done");
      }
      setWheelStatus("result");
    },
    [wheelStatus, runSubmission, settings, organizationId, resolvedPopupId, debug]
  );

  // The wheel has stopped on the wedge: a short beat to let it sink in,
  // then the success step (fresh mount → the theme's entry animation).
  const handleWheelLanded = useCallback(() => {
    if (wheelLandedRef.current) return;
    wheelLandedRef.current = true;
    const delayRaw = settings?.content_settings?.wheel?.result_delay_ms;
    const delay =
      typeof delayRaw === "number" && delayRaw >= 0
        ? Math.min(delayRaw, 8000)
        : WHEEL_DEFAULTS.result_delay_ms;
    // Play-first games land on the FORM (the prize is known, now the lead);
    // every other placement has already submitted, so: success.
    const next = stepAfterWheel(settings?.content_settings);
    setTimeout(() => {
      setPopupState((prev) => ({
        ...prev,
        isLoading: false,
        isTransitioning: false,
        currentStep: next,
      }));
      setWheelStatus("idle");
    }, delay);
  }, [settings?.content_settings]);

  const handleWheelSettled = useCallback(() => {
    setWheelStatus("idle");
  }, []);

  // Intro CTA: pure navigation — nothing validated, nothing persisted. With
  // `intro.button_url` set the CTA leaves for that page (same tab — the
  // popup IS the announcement, there is no form behind it); otherwise it
  // lands on the scratch page when one is playable, else straight on the form.
  const handleIntroAdvance = useCallback(() => {
    setGlobalError(null);
    emitPopupCtaClick(
      { organizationId, popupId: settings?.id || resolvedPopupId, debug },
      { step: "intro" }
    );
    const link = introCtaUrl(settings?.content_settings);
    if (link && typeof window !== "undefined") {
      try {
        window.location.assign(link);
        return;
      } catch {
        /* navigation blocked — fall through to the classic advance */
      }
    }
    setPopupState((prev) => ({
      ...prev,
      currentStep: stepAfterIntro(settings?.content_settings),
    }));
  }, [organizationId, settings?.id, settings?.content_settings, resolvedPopupId, debug]);

  // Question page answered: remember it, move to the next page or the gate.
  const handleQuestionAnswer = useCallback(
    (key: string, value: string) => {
      pendingAnswersRef.current = { ...pendingAnswersRef.current, [key]: value };
      const next = stepAfterQuestion(settings?.content_settings, questionIndex);
      setGlobalError(null);
      if (next === "question") setQuestionIndex((i) => i + 1);
      setPopupState((prev) => ({ ...prev, currentStep: next }));
      emitPopupCtaClick(
        { organizationId, popupId: settings?.id || resolvedPopupId, debug },
        { step: "question" }
      );
    },
    [settings, questionIndex, organizationId, resolvedPopupId, debug]
  );

  // Scratch CTA (post-reveal Claim): pure navigation to the gate — the
  // form, or the wheel when it carries the form inline.
  const handleScratchAdvance = useCallback(() => {
    setGlobalError(null);
    setPopupState((prev) => ({
      ...prev,
      currentStep: stepAfterScratch(settings?.content_settings),
    }));
    emitPopupCtaClick(
      { organizationId, popupId: settings?.id || resolvedPopupId, debug },
      { step: "scratch" }
    );
  }, [organizationId, settings?.id, settings?.content_settings, resolvedPopupId, debug]);

  // Gate submit. Deck popups: validate + hold the lead, advance to the deck —
  // nothing is persisted yet, so an abandoned deck never creates a half-lead.
  // Classic popups: straight through to runSubmission, byte-identical
  // behaviour to before the deck existed.
  const handleWelcomeSubmit = useCallback(
    async (formData: PopupFormData) => {
      const deck = settings?.content_settings?.deck;
      const deckPlayable =
        !!deck?.enabled &&
        (deck.cards || []).some((c) => c && c.image_url && c.key);

      if (deckPlayable) {
        pendingGateFormRef.current = formData;
        setGlobalError(null);
        setPopupState((prev) => ({ ...prev, currentStep: "deck" }));
        emitPopupCtaClick(
          { organizationId, popupId: settings?.id || resolvedPopupId, debug },
          { step: "welcome" }
        );
        return;
      }

      // Wheel AFTER the form: hold the lead, show the wheel; the spin submits.
      if (stepAfterWelcome(settings?.content_settings) === "wheel") {
        pendingGateFormRef.current = formData;
        setGlobalError(null);
        setPopupState((prev) => ({ ...prev, currentStep: "wheel" }));
        emitPopupCtaClick(
          { organizationId, popupId: settings?.id || resolvedPopupId, debug },
          { step: "welcome" }
        );
        return;
      }

      // Phone page after the form: hold the lead, ask for the number — that
      // page (or its skip link) fires the one submission.
      if (phonePagePlayable(settings?.content_settings)) {
        pendingGateFormRef.current = formData;
        setGlobalError(null);
        setPopupState((prev) => ({ ...prev, currentStep: "phone" }));
        emitPopupCtaClick(
          { organizationId, popupId: settings?.id || resolvedPopupId, debug },
          { step: "welcome" }
        );
        return;
      }

      await runSubmission(formData);
    },
    [settings, runSubmission, organizationId, resolvedPopupId, debug]
  );

  // Phone page: the held lead + the number (or without it, via the skip link).
  const handlePhoneSubmit = useCallback(
    (whatsappNumber: string) => {
      const base: PopupFormData = pendingGateFormRef.current || { name: "", email: "", whatsapp_number: "" };
      pendingGateFormRef.current = null;
      void runSubmission({ ...base, whatsapp_number: whatsappNumber });
    },
    [runSubmission]
  );
  const handlePhoneSkip = useCallback(() => {
    const base: PopupFormData = pendingGateFormRef.current || { name: "", email: "", whatsapp_number: "" };
    pendingGateFormRef.current = null;
    void runSubmission({ ...base, whatsapp_number: "" });
  }, [runSubmission]);

  // Deck finished: fold every card's verdict into the gate's form data and
  // fire the one real submission. Defensive empty base — the verdicts still
  // persist even if the gate form ref was somehow lost.
  const handleDeckComplete = useCallback(
    (answers: Record<string, "liked" | "disliked">) => {
      const base: PopupFormData = pendingGateFormRef.current || {
        name: "",
        email: "",
        whatsapp_number: "",
      };
      pendingGateFormRef.current = null;
      void runSubmission({
        ...base,
        extra_fields: { ...(base.extra_fields || {}), ...answers },
      });
    },
    [runSubmission]
  );

  // "Try again" after a failed submission, from a stage with no button of its
  // own. The stored payload already carries the deck verdicts / question
  // answers, so the retry submits exactly what failed.
  const handleRetrySubmit = useCallback(() => {
    const payload = failedSubmissionRef.current;
    if (!payload) return;
    void runSubmission(payload);
  }, [runSubmission]);

  const handleClose = useCallback(() => {
    setGlobalError(null);

    const currentStep = popupState.currentStep;

    setPopupState((prev) => ({
      ...prev,
      isVisible: false,
      currentStep: "closed",
    }));

    // Minimize to the teaser instead of fully dismissing when configured.
    // Runs in normal mode AND "customer" preview mode (the standard embed
    // snippet ships data-preview-mode="customer", so stores run in it) —
    // never in the static welcome/success/teaser previews, and not after a
    // successful submission.
    const teaser = normalized?.design.teaser;
    const isRealVisit =
      (!previewMode || previewMode === "customer") && !overrideSettings;
    if (
      teaser?.enabled &&
      teaser.mode !== "off" &&
      isRealVisit &&
      (currentStep !== "success" || teaserReward)
    ) {
      setTeaserVisible(true);
    }
    // Remember the dismissal so the teaser takes over on future page loads.
    if (isRealVisit && currentStep !== "success" && resolvedPopupId) {
      recordPopupDismissal(resolvedPopupId);
    }

    emitPopupClosed(
      { organizationId, popupId: resolvedPopupId, debug },
      { step: currentStep }
    );

    if (onClose) {
      onClose();
    }
  }, [
    onClose,
    organizationId,
    resolvedPopupId,
    debug,
    popupState.currentStep,
    normalized,
    previewMode,
    overrideSettings,
    teaserReward,
  ]);

  const handleTeaserClick = useCallback(() => {
    if (!settings) return;
    emitPopupTeaserClick({ organizationId, popupId: settings.id, debug });

    const teaser = normalized?.design.teaser;
    const memberUrl = (serial: string) =>
      `https://my.passes.co.za/my/${encodeURIComponent(serial)}`;

    // Post-signup reward state: the tab is the visitor's way BACK to what they
    // already earned — their member dashboard when we know their card and the
    // click action is "rewards", otherwise the success screen with their code.
    if (teaserReward) {
      const lifecycle = getPopupLifecycle(settings.id);
      if (teaser?.action === "rewards" && lifecycle.issuedPass?.serial) {
        window.open(memberUrl(lifecycle.issuedPass.serial), "_blank", "noopener");
        return;
      }
      setIssuedDiscountCode(lifecycle.issuedCode ?? issuedDiscountCode ?? null);
      setIssuedCodeStatus("done");
      if (lifecycle.wheelResult) {
        setWheelResult(lifecycle.wheelResult);
        setWheelExpiresAt(lifecycle.wheelExpiresAt ?? null);
      }
      const rememberedPass = lifecycle.issuedPass ?? issuedPass;
      if (rememberedPass) {
        setIssuedPass(rememberedPass as IssuedPass);
        setIssuedPassStatus("done");
      }
      setTeaserVisible(false);
      setPopupState((prev) => ({
        ...prev,
        isLoading: false,
        isVisible: true,
        currentStep: "success",
        hasBeenShown: true,
        showTime: new Date().toISOString(),
      }));
      // Deliberately NOT counted as an impression: re-reading your own reward
      // is not a new popup display, and counting it would skew conversion.
      return;
    }

    // Pre-signup click actions ("popup" and any unconfigured action fall
    // through to the historical open-the-popup behaviour).
    if (teaser?.action === "whatsapp" && teaser.actionNumber) {
      const digits = teaser.actionNumber.replace(/\D/g, "");
      const text = teaser.actionMessage
        ? `?text=${encodeURIComponent(teaser.actionMessage)}`
        : "";
      window.open(`https://wa.me/${digits}${text}`, "_blank", "noopener");
      return;
    }
    if (teaser?.action === "url" && teaser.actionUrl) {
      window.open(teaser.actionUrl, "_blank", "noopener");
      return;
    }
    if (teaser?.action === "rewards") {
      const lifecycle = getPopupLifecycle(settings.id);
      if (lifecycle.issuedPass?.serial) {
        window.open(memberUrl(lifecycle.issuedPass.serial), "_blank", "noopener");
        return;
      }
      // No card known yet — the popup IS the join flow; fall through.
    }

    setTeaserVisible(false);
    setPopupState((prev) => ({
      ...prev,
      isLoading: false,
      isVisible: true,
      currentStep: firstPopupStep(settings.content_settings),
      hasBeenShown: true,
      showTime: new Date().toISOString(),
    }));

    recordPopupImpression(settings.id);
    // A teaser click puts the real popup on screen — that's an impression.
    // Guarded to real visits (the builder preview never sets `settings`).
    if ((!previewMode || previewMode === "customer") && !overrideSettings) {
      reportPopupDisplay(settings.id);
    }
    if (onImpression) {
      onImpression();
    }
    emitPopupShown(
      { organizationId, popupId: settings.id, debug },
      { trigger_type: "teaser" }
    );
  }, [settings, organizationId, debug, onImpression, previewMode, overrideSettings, normalized, teaserReward, issuedDiscountCode, issuedPass]);

  const handleSuccessButtonClick = useCallback(() => {
    emitPopupCtaClick(
      { organizationId, popupId: resolvedPopupId, debug },
      { step: "success" }
    );
    // Close popup when success button is clicked
    handleClose();
  }, [handleClose, organizationId, resolvedPopupId, debug]);

  // ---------- render ----------

  if (!settings || !normalized) {
    return null;
  }

  const showTeaser = teaserVisible && !popupState.isVisible;

  if (!popupState.isVisible && !showTeaser) {
    return null;
  }

  return (
    <>
      {/* Keyframes + pseudo-class rules must exist whenever the teaser is
          mounted without the popup (the renderer also mounts this on its own;
          duplicate <style> tags are harmless). */}
      {showTeaser && <style>{RENDERER_CSS}</style>}
      {showTeaser && (
        <TeaserTab
          teaser={
            previewMode === "teaser"
              ? {
                  // Preview always shows something, even before the teaser is
                  // fully configured.
                  ...normalized.design.teaser,
                  enabled: true,
                  text: normalized.design.teaser.text || "Your teaser text",
                }
              : teaserReward
                ? {
                    // Post-signup reward state wears its own label.
                    ...normalized.design.teaser,
                    text:
                      normalized.design.teaser.postSignupText || "Your reward",
                  }
                : normalized.design.teaser
          }
          fontFamily={normalized.design.theme.fontFamily}
          onClick={previewMode === "teaser" ? () => {} : handleTeaserClick}
        />
      )}

      {popupState.isVisible && (
        <PopupRenderer
          currentStep={popupState.currentStep as "intro" | "scratch" | "question" | "welcome" | "phone" | "wheel" | "deck" | "success"}
          questionContent={playableQuestions(settings.content_settings)[questionIndex]}
          onQuestionAnswer={handleQuestionAnswer}
          phoneContent={settings.content_settings.phone_page}
          onPhoneSubmit={handlePhoneSubmit}
          onPhoneSkip={handlePhoneSkip}
          welcomeContent={settings.content_settings.welcome}
          successContent={settings.content_settings.success}
          introContent={settings.content_settings.intro}
          onIntroAdvance={handleIntroAdvance}
          scratchContent={settings.content_settings.scratch}
          onScratchAdvance={handleScratchAdvance}
          wheelContent={settings.content_settings.wheel}
          wheelStatus={wheelStatus}
          wheelResultIndex={wheelResult?.index ?? null}
          onWheelSpin={handleWheelSpin}
          onWheelLanded={handleWheelLanded}
          onWheelSettled={handleWheelSettled}
          wheelResult={wheelResult}
          wheelExpiresAt={wheelExpiresAt}
          deckContent={settings.content_settings.deck}
          onDeckComplete={handleDeckComplete}
          passPreview={settings.pass_preview}
          poweredByUrl={
            settings.powered_by
              ? `https://flowapt.com/?utm_source=popup&utm_medium=powered_by&utm_campaign=${encodeURIComponent(settings.organization_id || "")}`
              : undefined
          }
          popupId={settings.id}
          normalized={normalized}
          isVisible={true}
          issuedDiscountCode={issuedDiscountCode}
          issuedCodeStatus={issuedCodeStatus}
          issuedPass={issuedPass}
          issuedPassStatus={issuedPassStatus}
          onSubmit={handleWelcomeSubmit}
          onClose={handleClose}
          onButtonClick={handleSuccessButtonClick}
          isSubmitting={popupState.isLoading}
          isTransitioning={popupState.isTransitioning}
          showCloseButton={
            !popupState.isLoading &&
            !popupState.isTransitioning &&
            // never mid-spin: the submission is in flight and about to land
            !(popupState.currentStep === "wheel" && wheelStatus !== "idle")
          }
          previewMode={previewMode}
          globalErrorMessage={globalError || undefined}
          onRetrySubmit={failedSubmissionRef.current ? handleRetrySubmit : undefined}
          editMode={editMode && (previewMode === "welcome" || previewMode === "success")}
        />
      )}
    </>
  );
};

export default PopupWidget;
